import React, { useEffect, useState } from 'react';
import get from 'lodash/get';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { useFieldMap } from '@aperza/ui';
import { ObjectUtil, NumberUtil, StringUtil } from '@aperza/util';
import { pageBlockPropType } from '../../../propType/CustomPagePropType';
import CustomPageDetailContentWrapper from './CustomPageDetailContentWrapper';
import FieldMapForm from '../../form/FieldMapForm';
import CustomPageDetailContentFormSuccess from './CustomPageDetailContentFormSuccess';
import CustomPageDetailContentFormError from './CustomPageDetailContentFormError';
import { Constraint } from '@aperza/validator';

const createFieldMapConfig = (forms) => {

  let config = {};
  if (forms && Array.isArray(forms.formsItems)) {
    forms.formsItems.forEach((formsItem, index) => {
      let controlType = 'input';
      if (formsItem.formsItemType === 'SINGLE_TEXTBOX') {
        controlType = 'input';
      } else if (formsItem.formsItemType === 'SINGLE_SELECT') {
        controlType = 'select';
      }
      let layout = undefined;
      // if (formsItem.formsItemAnswerLayoutType === 'HORIZONAL') {
      //   layout = 'horizontal';
      // }

      let required = formsItem.formsItemAnswerRequired === 'REQUIRED';

      let formOptions = undefined;
      if (Array.isArray(formsItem.formsItemAnswers)) {
        formOptions = formsItem.formsItemAnswers.filter(ans => ans.formsItemAnswerDataType === 'TEXT' && ans.formsItemAnswerId)
          .map(formsItemAnswer => {
            return {
              value: '' + formsItemAnswer.formsItemAnswerId,
              label: formsItemAnswer.formsItemAnswerTitle ? formsItemAnswer.formsItemAnswerTitle : '' + formsItemAnswer.formsItemAnswerId,
            };
          });
      }

      if (formsItem.formsItemMatrixOn === 'ON' && Array.isArray(formsItem.formsItemMatrices) && formsItem.formsItemMatrices.length > 0) {
        formsItem.formsItemMatrices.forEach(matrix => {
          config[formsItem.formsItemId + '::' + matrix.formsItemMatrixId] = {
            fieldName: '' + formsItem.formsItemId + '::' + matrix.formsItemMatrixId,
            fieldNameId: matrix.formsItemMatrixTitle,
            parentFieldName: '' + formsItem.formsItemId,
            parentFieldNameId: '' + formsItem.formsItemTitle,
            controlType,
            layout,
            formOptions,
            required,
            constraint: required ? Constraint.required : undefined,
          };
        });
      } else {
        config[formsItem.formsItemId] = {
          fieldName: '' + formsItem.formsItemId,
          fieldNameId: formsItem.formsItemTitle,
          controlType,
          layout,
          formOptions,
          required,
          constraint: required ? Constraint.required : undefined,
        };
      }

    });
  }
  return config;
};

const SubmitCustomPageFormsAnswerMutation = gql`
    mutation SubmitCustomPageFormsAnswerMutation($input: SubmitCustomPageFormsAnswerInput!) {
        submitCustomPageFormsAnswer(input: $input) {
            data {
                formsId
                formsCompletePageContent
                formsCompletePageDownloadUrl
                formsCompletePageRedirectUrl
                formsCompletePageType
            }
        }
    }
`;

const CustomPageDetailContentForm = (props) => {

  let {
    pageBlock = {},
  } = props;

  let {
    forms = [],
  } = pageBlock;

  let { fieldMap, updateFieldValue, validateFieldMap, validateField, resetFieldMap } = useFieldMap(
    createFieldMapConfig(forms),
    {},
  );

  const [submit, { data, loading }] = useMutation(SubmitCustomPageFormsAnswerMutation);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  const onBlur = (fieldName, value, inputValue) => {
    updateFieldValue(fieldName, value);
    //validateField(fieldName); //TODO check when to enable this https://aperza.atlassian.net/browse/AB-9243
  };

  const onChange = (fieldName, value, inputValue) => {
    updateFieldValue(fieldName, value);
  };

  const onReset = () => {
    resetFieldMap();
  };

  const onSubmit = async () => {
    let errorCount = validateFieldMap(true);
    if (errorCount === 0) {
      let formsItemAnswers = Object.keys(fieldMap).map((key: string) => { //Submit answers even for optional
        let formsItemId = undefined;
        let formsItemMatrixId = undefined;
        let answerText = undefined;
        let formsItemAnswerId = 0; //Default value including for empty optional field
        if (key.includes('::')) {
          let parts = key.split('::');
          formsItemId = parseInt(parts[0]);
          formsItemMatrixId = parseInt(parts[1]);
        } else {
          formsItemId = parseInt(key);
        }

        let field = fieldMap[key];

        if (field.controlType === 'input') {
          if (StringUtil.isBlank(field.value)) {
            formsItemAnswerId = 0;
          } else {
            formsItemAnswerId = 3; //3: text box, 4: comment box https://aperza.atlassian.net/browse/AB-9097
            answerText = field.value;
          }
        } else {
          formsItemAnswerId = field.value ? parseInt(field.value) : 0;
        }

        let answer = {
          formsItemId,
          formsItemMatrixId,
          answerText,
          formsItemAnswerId,
        };
        return ObjectUtil.removeKeysWithUndefined(answer);
      });

      let formData = {
        pageBlockId: pageBlock.pageBlockId,
        formsAnswer: {
          formsId: pageBlock.forms.formsId,
          formsItemAnswers: formsItemAnswers,
        },
      };

      try {
        let response = await submit({
          variables: {
            input: {
              payload: JSON.stringify(formData),
            },
          },
        });

        if (get(response, 'data.submitCustomPageFormsAnswer.data.formsCompletePageType') === 'DISPLAY_COMPLETE_PAGE') {
          setSuccessMessage(get(response, 'data.submitCustomPageFormsAnswer.data.formsCompletePageContent'));
          setShowDialog(true);
        } else {
          setShowErrorDialog(true);
        }
      } catch (e) {
        setShowErrorDialog(true);
      }

    }
  };

  return (
    <CustomPageDetailContentWrapper pageBlock={pageBlock}>
      {showErrorDialog && <CustomPageDetailContentFormError />}
      {!showDialog && !showErrorDialog &&
      <FieldMapForm fieldMap={fieldMap} onBlur={onBlur} onChange={onChange} onSubmit={onSubmit} onReset={onReset}
                    loading={loading}/>
      }
      {showDialog &&
        <CustomPageDetailContentFormSuccess messageId={successMessage}/>
      }
    </CustomPageDetailContentWrapper>
  );

};

CustomPageDetailContentForm.fragments = {
  forms: gql`
      fragment CustomPageDetailContentForm_forms on CustomPageForms {
          formsId
          formsCompletePageType
          formsCompletePageContent
          formsCompletePageDownloadUrl
          formsCompletePageRedirectUrl
          formsItems {
              formsItemId
              formsItemType
              formsItemTitle
              formsItemDescription
              formsItemAnswerLayoutType
              formsItemAnswerRequired
              formsItemAnswerSelectionMin
              formsItemAnswerSelectionMax
              formsItemMatrixOn
              formsItemMatrixCount
              formsItemMatrices {
                  formsItemMatrixId
                  formsItemMatrixTitle
                  formsItemDescription
                  displayOrder
              }
              formsItemAnswers {
                  formsItemAnswerId
                  formsItemAnswerTitle
                  formsItemAnswerDescription
                  formsItemAnswerImageUrl
                  formsItemAnswerDataType
                  displayOrder
              }
              formsOtherAnswerAdd
              formsOtherAnswerDisplayType
              formsOtherAnswerRequired
              formsOtherAnswer
              displayOrder
          }
      }
  `,
};

CustomPageDetailContentForm.propTypes = {
  pageBlock: pageBlockPropType(),
};

export default CustomPageDetailContentForm;
